import { graphHooks } from '@tunasong/graph-lib/react'
import { resolveDefaultPrompts, useAgentTurnStream } from '@tunasong/plugin-agent/integration'
import { useCurrentUser } from '@tunasong/redux'
import type { Agent, Comment, Persisted } from '@tunasong/schemas'
import { isInUsersAgentChat } from '@tunasong/schemas'
import type { FC } from 'react'
import styles from './agent-chat-empty-header.module.css'

export interface AgentChatEmptyHeaderProps {
  /** The agent currently surfaced in the sidebar. */
  agent: Persisted<Agent>
  /** Called when the user clicks a prompt chip. Receives the prompt text. */
  onSelectPrompt: (prompt: string) => void
}

/**
 * Starter prompts for an empty thread — identity lives in
 * AgentIdentityHeader. Renders up to 3 starter prompts as full-width rows, and C5's hint
 * (chat makeover #2718). Returns null
 * when the agent has any comments — so the header disappears once a
 * conversation exists.
 *
 * The empty-state read goes through `useEntityChildren(filter: 'comment')`
 * rather than `useChat` to avoid double-mounting the chat hook for what is
 * essentially a length check. Cold-cache returns `entities: []` which we
 * treat as "empty" — same behaviour as Chat itself in that window.
 *
 * Pre-fill rather than auto-submit (Joni's call, Nielsen #3 — user agency).
 * Chip click fires `onSelectPrompt` so the parent can pipe text into the
 * composer below.
 */
export const AgentChatEmptyHeader: FC<AgentChatEmptyHeaderProps> = props => {
  const { agent, onSelectPrompt } = props
  const { userId } = useCurrentUser()

  const { entities: comments } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agent.id,
    filter: 'comment',
  })

  // Phase 2 (sidebar live tokens): when a stream is already in flight for the
  // agent the user is actively conversing — the empty-state header would look
  // like leftover scaffolding above the live response. Hide it.
  const liveStream = useAgentTurnStream(agent.id)

  // Empty means this user's current conversation (per-user, and after their "New conversation"), as the
  // thread below shows it, so the starter prompts come back when they start over
  if (comments?.some(comment => isInUsersAgentChat(comment, agent, userId ?? ''))) {
    return null
  }
  if (liveStream) {
    return null
  }

  const prompts = resolveDefaultPrompts(agent)

  return (
    <div className={styles.container}>
      {prompts.length > 0 ? (
        <div role="group" aria-label="Example prompts">
          <ul className={styles.prompts}>
            {prompts.map(prompt => (
              <li key={prompt}>
                <button
                  type="button"
                  className={styles.prompt}
                  aria-label={`Use prompt: ${prompt}`}
                  onClick={() => onSelectPrompt(prompt)}
                >
                  {prompt}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {/* C5 (chat makeover #2718): the anchored ask is the agents' best use, and nothing else teaches it */}
      <p className={styles.hint}>Select a line, chord or section first to ask about it.</p>
    </div>
  )
}
